import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectCountries } from "./selectCitySlice";
import CityList from "./CityList";

import { findClosestCity } from "../../api/countries/findClosestCities";
import { getCityFullName } from "./getCityFullName";
import { useTranslation } from "react-i18next";

export const initialState = {
    city: "",
    district: "",
    area: "",
    country: "",
    countryHasCities: false,
}

const debouncer = (delay, cb) => {
    let timeout = null

    return (value, country) => {
        clearTimeout(timeout)
        timeout = setTimeout(() => {
            cb(value, country)
        }, delay)
    }
}

const locationUnitsLanguages = {
    RU: "ru-name",
    TR: "tr-name",
}

export const selectCityInputLanguages = {
    RU: "ru",
    TR: "tr",
}

const SelectCity = ({
    state,
    setState,
    disabled,
    labelClassNames,
    inputsClassNames,
    inputBlockClassNames,
    containerClassName,
    selectClassName,
    countryInputLabel,
    cityInputLabel,
    language,
}) => {
    const {t} = useTranslation()
    const countries = useSelector(selectCountries);
    const [countryHasCities, setCountryHasCities] = useState();

    language = language ?? selectCityInputLanguages.TR;

    //setting language for location units
    const locationUnitsLanguage = useMemo(() => {
        if (language === selectCityInputLanguages.TR) {
            return locationUnitsLanguages.TR;
        }

        //default language is RU
        return locationUnitsLanguages.RU;
    }, [language]);
    
    //setting has country cities or not
    useEffect(() => {
        let country = countries.find(
            (country) => country[locationUnitsLanguages.RU] === state.country || country[locationUnitsLanguages.TR] === state.country
        );
        if (!country || country["has-cities"] === false) {
            return setCountryHasCities(false);
        }
        setCountryHasCities(true);
    }, [state.country, countries])
    
    const [cityInputValue, setCityInputValue] = useState("");
    const [cityList, setCityList] = useState([]);

    //filling input with city full name on change state
    useEffect(() => {
        if(!state) return;

        if (state.country === "") return;

        let country = countries.find(
            (country) => country[locationUnitsLanguages.RU] === state.country || country[locationUnitsLanguages.TR] === state.country
        );

        if(!country || !state.city) return;

        let cityFullName = getCityFullName(state, language);
        setCityInputValue(cityFullName);
        setCityList([])
        return;
    }, [countries, language, state]);
    
    //calling api
    const debounce = useMemo(() => {
        const callApi = async (value, country) => {
            let resp = await findClosestCity(value, country[locationUnitsLanguage], language);
            if (!resp.ok) {
                return;
            }
            
            let cities = resp.cities;
            setState((prev) => ({
                ...prev,
                city: null,
                district: null,
                area: null,
                cityFullName: "",
            }))
            
            setCityList(cities);
        }
    
        return debouncer(200, callApi)
    }, [language, locationUnitsLanguage, setState])
    
    //handling input change
    const handleInputChange = async (e) => {
        let value = e.target.value;
        setCityInputValue(value);

        if (value.length < 3) {
            setCityList([]);
            return;
        }

        let country = countries.find(
            (country) => country[locationUnitsLanguages.RU] === state.country || country[locationUnitsLanguages.TR] === state.country
        );
        debounce(value, country)
        
    };
    
    const handleSelectChange = (e) => {
        console.log(e.target.value)
        setState({
            country: e.target.value,
            city: null,
            district: null,
            area: null,
            cityFullName: "",
        });
        setCityList([]);
        setCityInputValue("");
    };

    return (
        <div className={containerClassName}>
            <div className={inputBlockClassNames}>
                <span className={labelClassNames}>{ countryInputLabel ?? t("select_city.country")}</span>
                <select
                    disabled={disabled ?? false}
                    className={selectClassName}
                    name="countries"
                    id="countries"
                    value={state.country}
                    onChange={handleSelectChange}
                >
                    {countries.map((country) => (
                        <option name="" value={country[locationUnitsLanguage]} key={country[locationUnitsLanguage]}>
                            {`${country[locationUnitsLanguages.TR]} (${country[locationUnitsLanguages.RU]})`}
                        </option>
                    ))}
                </select>
            </div>

            {countryHasCities && (
                <div className="">
                    <div className={inputBlockClassNames}>
                        <span className={labelClassNames}>{cityInputLabel ?? t("select_city.city")}</span>
                        <input
                            disabled={disabled ?? false}
                            className={inputsClassNames}
                            type="text"
                            placeholder={t("select_city.city_placeholder")}
                            value={cityInputValue}
                            onChange={handleInputChange}
                        />
                    </div>
                    {cityList.length > 0 && (
                        <CityList
                            inputValue={cityInputValue}
                            cityList={cityList}
                            setState={setState}
                            language={language}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default SelectCity;
